import DatasetSelector from "@/components/EcoInventDatasetSelector/DatasetSelector";
import Message from "@/components/Message/Message";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import Skeleton from "@/components/Skeleton/Skeleton";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import { useUserSettings } from "@/hooks/useUserSettings";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, message, Space, Tabs } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DatasetDetails from "../../impact_references/DatasetDetails";
import DeleteRootImpactReferenceAction from "./DeleteRootImpactReferenceAction";
import styles from "./ImpactReferenceDetails.module.scss";
import {
  PostRootImpactReference,
  RootImpactReferenceExchange,
  useGetRootImpactReference,
  useInsertRootImpactReference,
  useUpdateRootImpactReference,
} from "./useRootImpactReference";

export interface RootImpactReferenceForm {
  elementary_eco_invent_dataset_id: number | null;
  name: string;
  geography: string;
  unit: string;
  note: string;
  exchanges: RootImpactReferenceExchange[];
}

export default function RootImpactReference() {
  const id = Number(useParams().referenceId);
  const addReferenceId = Number(useParams().addReferenceId);
  const [form] = Form.useForm<RootImpactReferenceForm>();
  const analytics = useAnalytics();
  const exchanges = useWatch("exchanges", form);
  const insert = useInsertRootImpactReference();
  const update = useUpdateRootImpactReference();
  const location = useLocation();
  const { data: userSettings } = useUserSettings();

  const { isLoading, data } = useGetRootImpactReference({
    id: id || addReferenceId,
    fromEcoInventDataset: Boolean(addReferenceId),
  });

  const navigate = useNavigate();
  const t = useTranslation();

  const referenceName = !addReferenceId
    ? data?.dataset.name || ""
    : t.impactReferences.root.addTitle;

  analytics.track("Root Impact Reference Viewed");

  useEffect(() => {
    form.setFieldsValue({
      ...data?.dataset,
      exchanges: data?.exchanges,
    });
  }, [data]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((data) => {
        const post: PostRootImpactReference = {
          dataset: {
            note: data.note || "",
            name: data.name,
            geography: data.geography || "",
            unit: data.unit,
            elementary_eco_invent_dataset_id: data.elementary_eco_invent_dataset_id,
          },
          exchanges: data.exchanges,
        };

        if (id) {
          update
            .mutateAsync({ id, post })
            .then(() => {
              analytics.track("Root Impact Reference Updated");
              message.success(t.impactReferences.root.updatedSuccess);
              if (location.key !== "default") {
                navigate(-1);
              } else {
                navigate(`/admin/root_impact_references`);
              }
            })
            .catch((error) => {
              message.error(error.response.data.message);
            });
        } else {
          insert
            .mutateAsync({ post })
            .then(() => {
              analytics.track("Root Impact Reference Created");
              message.success(t.impactReferences.root.createdSuccess);
              navigate(`/admin/root_impact_references`);
            })
            .catch((error) => {
              message.error(t.errors.general);
            });
        }
      })
      .catch(() => {
        message.error(t.impactReferences.root.formValidationFailedError);
      });
  };

  const cancelSaveButtons = (
    <>
      <Link to="/admin/root_impact_references">
        <Button>{t.general.cancel}</Button>
      </Link>
      <Button
        type="primary"
        onClick={onSubmit}
        disabled={isLoading || insert.isLoading || update.isLoading}
        loading={insert.isLoading || update.isLoading}
      >
        {t.general.save}
      </Button>
    </>
  );

  if (userSettings && userSettings?.role !== "root_admin") {
    return <Message type="error">You do not have access to this page.</Message>;
  }

  return (
    <div className={styles.ImpactReferenceDetails}>
      <PageHeader
        loading={isLoading}
        title={referenceName}
        right={cancelSaveButtons}
      />
      <Form
        form={form}
        autoComplete={"off"}
      >
        <div className={styles.form}>
          <div className={styles.left}>
            <DatasetDetails
              isCustom={true}
              isLoading={isLoading}
              title={t.impactReferences.root.rootImpactReference}
              description={t.impactReferences.root.description}
            />

            <div className={styles.buttons}>
              <DeleteRootImpactReferenceAction
                id={id}
                onSuccess={() => navigate("/admin/root_impact_references")}
              >
                <Button
                  type={"link"}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!id || isLoading}
                >
                  {t.general.delete}
                </Button>
              </DeleteRootImpactReferenceAction>
              <span>
                <Space>{cancelSaveButtons}</Space>
              </span>
            </div>
          </div>
          <div className={styles.right}>
            <Form.Item
              label={"Elementary exchange basis"}
              hidden={true}
              name={"elementary_eco_invent_dataset_id"}
            />
            <h2>{t.impactReferences.root.exchanges}</h2>
            <Tabs
              items={[
                {
                  label: t.impactReferences.root.intermediateExchanges,
                  key: "intermediate",
                  children: (
                    <div className={styles["exchanges-list"]}>
                      <Form.List name="exchanges">
                        {(fields, { add, remove }) => (
                          <>
                            <div>
                              <DatasetSelector
                                onSelect={(dataset) => {
                                  add({
                                    id: dataset.id,
                                    dataset: {
                                      id: dataset.id,
                                      name: dataset.name,
                                      geography: dataset.geography,
                                      unit: dataset.unit,
                                    },
                                    amount: undefined,
                                    not_absolute: false,
                                  });
                                  message.success(t.impactReferences.root.exchangeAdded);
                                }}
                              >
                                <Button icon={<PlusOutlined />}>Add</Button>
                              </DatasetSelector>
                            </div>
                            <div className={styles.exchanges}>
                              {!exchanges && (
                                <div className={styles.exchange}>
                                  <Skeleton style={{ width: 200, height: 30 }} />
                                </div>
                              )}
                              {exchanges && exchanges.length === 0 && (
                                <div className={styles.exchange}>No intermediate exchanges.</div>
                              )}
                              {fields.map((field, index) => (
                                <Form.Item
                                  required={false}
                                  key={field.key}
                                  noStyle
                                >
                                  <div className={styles.exchange}>
                                    <div className={styles.data}>
                                      <Form.Item
                                        name={[field.name, "dataset", "name"]}
                                        valuePropName="children"
                                        noStyle
                                      >
                                        <span
                                          className={styles.title}
                                          children={<></>}
                                        />
                                      </Form.Item>
                                      <span className={styles.details}>
                                        <Form.Item
                                          name={[field.name, "dataset", "geography"]}
                                          valuePropName="children"
                                          noStyle
                                        >
                                          <span />
                                        </Form.Item>
                                        <span>•</span>
                                        <Form.Item
                                          name={[field.name, "dataset", "unit"]}
                                          valuePropName="children"
                                          noStyle
                                        >
                                          <span />
                                        </Form.Item>
                                      </span>
                                      <Form.Item
                                        name={[field.name, "amount"]}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Amount can not be empty.",
                                          },
                                        ]}
                                        noStyle
                                      >
                                        <InputNumber
                                          controls={false}
                                          style={{ width: "200px" }}
                                        />
                                      </Form.Item>
                                    </div>
                                    <div className={styles.buttons}>
                                      <Form.Item
                                        name={[field.name, "not_absolute"]}
                                        valuePropName="checked"
                                        noStyle
                                      >
                                        <SwitchWithLabel size="small">Not absolute</SwitchWithLabel>
                                      </Form.Item>
                                      <Button
                                        danger
                                        onClick={() => remove(field.name)}
                                      >
                                        <DeleteOutlined />
                                      </Button>
                                    </div>
                                  </div>
                                </Form.Item>
                              ))}
                            </div>
                          </>
                        )}
                      </Form.List>
                    </div>
                  ),
                },
                {
                  label: t.impactReferences.root.elementaryExchanges,
                  key: "elementary",
                  children: (
                    <div className={styles["exchanges-list"]}>
                      <Message type="note">
                        Elementary exchanges are copied from the original reference this root impact
                        reference was based on and cannot be changed.
                      </Message>
                      <div className={styles.exchanges}>
                        {!data?.elementary_exchanges && (
                          <div className={styles.exchange}>
                            <Skeleton style={{ width: 200, height: 30 }} />
                          </div>
                        )}
                        {data?.elementary_exchanges && data?.elementary_exchanges.length === 0 && (
                          <div className={styles.exchange}>No elementary exchanges.</div>
                        )}
                        {data?.elementary_exchanges.map((exchange) => (
                          <div className={styles.exchange}>
                            <div className={styles.data}>
                              <span className={styles.title}>{exchange.name}</span>
                              <span className={styles.details}>
                                <span>
                                  {exchange.amount} {exchange.unit}
                                </span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Form>
    </div>
  );
}
